import { registerQuery } from '@adornis/baseql/metadata/register.js';
import type { BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { SNClientOption, SNFieldEngineerOption } from '../db/SNLookups.js';

// Note: `@nsc/servicenow/smartteam-api.js` runs `new Config(...)` at module load and
// would crash if its keys aren't transferred to the client. We dynamic-import inside
// each resolver so esbuild splits it into a server-only chunk that the client never
// actually fetches. Same pattern as `entrypoints/smartteams-app/src/api/ticket-queries.ts`.

/**
 * Fetch all field engineers from ServiceNow. Drives the searchable dropdown
 * in the admin backend so we never have to type a sys_id by hand.
 */
export const getServiceNowEngineers = registerQuery({
  operationName: 'getServiceNowEngineers',
  type: () => [SNFieldEngineerOption],
  params: [],
  resolve: () => async (_gql: BaseQLSelectionSet<SNFieldEngineerOption>) => {
    const { getEngineerList } = await import('@nsc/servicenow/smartteam-api.js');
    const list = await getEngineerList();
    return list.map(
      e =>
        new SNFieldEngineerOption({
          id: e.id,
          firstName: e.first_name,
          lastName: e.last_name,
          email: e.email,
        }),
    );
  },
});

/**
 * Fetch all clients from ServiceNow. Used to bind a SmartTeamsCustomer
 * to a ServiceNow client sys_id via dropdown.
 */
export const getServiceNowClients = registerQuery({
  operationName: 'getServiceNowClients',
  type: () => [SNClientOption],
  params: [],
  resolve: () => async (_gql: BaseQLSelectionSet<SNClientOption>) => {
    const { getClientList } = await import('@nsc/servicenow/smartteam-api.js');
    const list = await getClientList();
    return list.map(
      c =>
        new SNClientOption({
          id: c.id,
          name: c.name,
          code: c.code,
          parentCompanyId: c.parent_company_id || undefined,
          parentCompany: c.parent_company || undefined,
        }),
    );
  },
});
